import { useIdentity } from '~/hooks/identity';
import useReduxState from '~/hooks/useReduxState';
import useReadLocalStorage from '~/hooks/useReadLocalStorage';

import { auth, AuthMethod } from '~/api/axios';
import { useCheckInvitationToken } from './invitationToken';

type InvitationPageState = {
  isLoading: boolean;
  isError: boolean;
  showPages: {
    smsOtp: boolean | null;
    setPassword: boolean | null;
  };
  loginAfterAuthActions: boolean;
  data: {
    emailAddress: string | null;
    businessLegalName: string | null;
    phoneNumber: string | null;
    password: string | null;
  };
};

type InvitationPageActions = {
  smsOtpSuccess(params: { identityHasPassword: boolean }): void;
  setPasswordSuccess(password: string): void;
};

export const useInvitationPageState = (): [InvitationPageState, InvitationPageActions] => {
  const url = new URL(window.location.href);
  const token = url.searchParams.get('token') as string;
  const [pageState, setPageState] = useReduxState<InvitationPageState>('invitationPage', {
    isLoading: true,
    isError: false,
    showPages: {
      smsOtp: null,
      setPassword: null,
    },
    loginAfterAuthActions: false,
    data: {
      emailAddress: null,
      businessLegalName: null,
      phoneNumber: null,
      password: null,
    },
  });

  useCheckInvitationToken(token, {
    onSuccess(res) {
      setPageState((prev) => ({
        ...prev,
        isLoading: false,
        isError: false,
        showPages: {
          smsOtp: res.authActions.smsOtp || false,
          setPassword: res.authActions.setPassword || false,
        },
        data: {
          emailAddress: res.emailAddress,
          phoneNumber: res.phoneNumber || null,
          businessLegalName: res.businessLegalName,
          password: null,
        },
      }));
    },
    onError() {
      setPageState((prev) => ({
        ...prev,
        isLoading: false,
        isError: true,
      }));
    },
  });

  const actions: InvitationPageActions = {
    smsOtpSuccess({ identityHasPassword }) {
      setPageState((prev) => ({
        ...prev,
        showPages: {
          ...prev.showPages,
          smsOtp: false,
          setPassword: !identityHasPassword,
        },
        data: {
          ...prev.data,
        },
      }));
    },
    setPasswordSuccess(password: string) {
      setPageState((prev) => ({
        ...prev,
        showPages: {
          ...prev.showPages,
          setPassword: false,
        },
        data: {
          ...prev.data,
          password,
        },
      }));
    },
  };

  return [pageState, actions];
};

export const authCase = {
  WAITING: 'Waiting for invitation token check',
  NEED_TERMINATE_CURRENT_SESSION: 'Need to terminate current session to continue enrollment',
  NEED_TERMINATE_CURRENT_SESSION_AND_LOGIN:
    'Need to terminate current session and log in to continue enrollment',
  NEED_LOGIN: 'Not logged in, need login with login and password',
  CONFIRM_PASSWORD:
    'Authenticated with SMS OTP and email from invitation not match with Api Identity email',
  CURRENT_SESSION_VALID: 'Current session is valid. Can accept invitation without auth actions',
} as const;

type WaitingConst = typeof authCase.WAITING;
type NeedTerminateAndLoginConst = typeof authCase.NEED_TERMINATE_CURRENT_SESSION;
type NeedTerminateAndLoginAndLoginConst = typeof authCase.NEED_TERMINATE_CURRENT_SESSION_AND_LOGIN;
type NeedLoginConst = typeof authCase.NEED_LOGIN;
type ConfirmPasswordConst = typeof authCase.CONFIRM_PASSWORD;
type CurrentSessionValidConst = typeof authCase.CURRENT_SESSION_VALID;

export const useCheckCurrentSession = (
  emailAddress: string | undefined | null
): {
  isLoading: boolean;
  authCase:
    | WaitingConst
    | NeedTerminateAndLoginConst
    | NeedTerminateAndLoginAndLoginConst
    | NeedLoginConst
    | ConfirmPasswordConst
    | CurrentSessionValidConst;
} => {
  const accessToken = auth.getToken();
  const identityQuery = useIdentity({ enabled: !!accessToken });
  const [pageState] = useInvitationPageState();
  const authMethod = useReadLocalStorage<AuthMethod | undefined>(auth.authMethod);

  if (!emailAddress) {
    return {
      isLoading: false,
      authCase: authCase.WAITING,
    };
  }

  if (accessToken) {
    if (!identityQuery.data) {
      return {
        isLoading: false,
        authCase: authCase.NEED_LOGIN,
      };
    }
    if (identityQuery.data.emailAddress === emailAddress) {
      return {
        isLoading: false,
        authCase: authCase.CURRENT_SESSION_VALID,
      };
    }
    if (
      !!identityQuery.data.emailAddress &&
      identityQuery.data.emailAddress !== emailAddress &&
      !pageState.showPages.smsOtp &&
      authMethod === 'password'
    ) {
      return {
        isLoading: false,
        authCase: authCase.CURRENT_SESSION_VALID,
      };
    }
    if (
      !!identityQuery.data.emailAddress &&
      identityQuery.data.emailAddress !== emailAddress &&
      !pageState.showPages.smsOtp
    ) {
      return {
        isLoading: false,
        authCase: authCase.CONFIRM_PASSWORD,
      };
    }
    if (!pageState.showPages.smsOtp && !pageState.showPages.setPassword) {
      return {
        isLoading: false,
        authCase: authCase.NEED_TERMINATE_CURRENT_SESSION_AND_LOGIN,
      };
    }
    if (!pageState.showPages.smsOtp) {
      return {
        isLoading: false,
        authCase: authCase.NEED_LOGIN,
      };
    }
    return {
      isLoading: false,
      authCase: authCase.NEED_TERMINATE_CURRENT_SESSION,
    };
  }
  return {
    isLoading: false,
    authCase: authCase.NEED_LOGIN,
  };
};
